import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useReducedMotion } from "../../lib/useReducedMotion";

interface MotionSettingsValue {
  reduced: boolean;
  toggleReduced: () => void;
}

const MotionSettingsContext = createContext<MotionSettingsValue | null>(null);

function loadPreference(): boolean | null {
  const saved = window.localStorage.getItem("athlete-motion");
  if (saved === "reduced") return true;
  if (saved === "full") return false;
  return null;
}

export function MotionSettingsProvider({ children }: { children: ReactNode }) {
  const systemReduced = useReducedMotion();
  const [preference, setPreference] = useState<boolean | null>(() => loadPreference());
  const reduced = preference ?? systemReduced;

  useEffect(() => {
    document.documentElement.dataset.motion = reduced ? "reduced" : "full";
  }, [reduced]);

  const value = useMemo<MotionSettingsValue>(
    () => ({
      reduced,
      toggleReduced: () => {
        setPreference((current) => {
          const next = !(current ?? systemReduced);
          window.localStorage.setItem("athlete-motion", next ? "reduced" : "full");
          return next;
        });
      }
    }),
    [reduced, systemReduced]
  );

  return <MotionSettingsContext.Provider value={value}>{children}</MotionSettingsContext.Provider>;
}

export function useMotionSettings(): MotionSettingsValue {
  const value = useContext(MotionSettingsContext);
  if (!value) {
    throw new Error("useMotionSettings must be used inside MotionSettingsProvider");
  }
  return value;
}
